import { useState } from 'react';
import {
  apiErrorText,
  useCreateTenderEstimateMutation,
  useListSmetaContextOptionsQuery,
  useListVolumeChecksQuery,
} from '@shared/api';
import { Card } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import { Field, Input, Label } from '@shared/ui/Input';
import { useAgentPermission } from '@entities/user';
import {
  Err,
  FileHint,
  FormRow,
  FormSelect,
  Select,
  SubmitWrapper,
} from './styled';

interface TenderCreateFormProps {
  onCreated: (checkId: string) => void;
}

export function TenderCreateForm({ onCreated }: TenderCreateFormProps) {
  const [objectName, setObjectName] = useState('');
  const [smetaCode, setSmetaCode] = useState('');
  const [region, setRegion] = useState('');
  const [objectType, setObjectType] = useState('');
  const [inputKind, setInputKind] = useState('grand_smeta_skeleton');
  const [volumeCheckId, setVolumeCheckId] = useState('');
  const [volumeSource, setVolumeSource] = useState('rd');
  const [precontractId, setPrecontractId] = useState('');
  const [contractId, setContractId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [calcMode, setCalcMode] = useState('works_and_materials');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { can_write } = useAgentPermission('smeta');
  const volumeChecksQ = useListVolumeChecksQuery({ limit: 50 });
  const contextOptionsQ = useListSmetaContextOptionsQuery();
  const [createEstimate, { isLoading: creating }] = useCreateTenderEstimateMutation();

  const volumeChecks = (volumeChecksQ.data?.items ?? []).filter((v) => v.status === 'completed');
  const contextContracts = contextOptionsQ.data?.contracts ?? [];
  const contextProjects = contextOptionsQ.data?.projects ?? [];
  const contextPrecontracts = contextOptionsQ.data?.precontracts ?? [];

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (inputKind === 'volume_check') {
      if (!volumeCheckId) {
        setError('Выберите проверку объёмов (Функция 1) как источник позиций.');
        return;
      }
    } else if (!file) {
      setError('Приложите файл перечня работ тендера (Excel/PDF).');
      return;
    }
    const fd = new FormData();
    fd.append('object_name', objectName);
    if (smetaCode.trim()) fd.append('smeta_code', smetaCode);
    if (region.trim()) fd.append('region', region);
    if (objectType.trim()) fd.append('object_type', objectType);
    if (precontractId) fd.append('precontract_id', precontractId);
    if (contractId) fd.append('contract_id', contractId);
    if (projectId) fd.append('project_id', projectId);
    fd.append('input_kind', inputKind);
    fd.append('calc_mode', calcMode);
    if (inputKind === 'volume_check') {
      fd.append('volume_check_id', volumeCheckId);
      fd.append('volume_source', volumeSource);
    } else if (file) {
      fd.append('file', file);
    }
    try {
      const res = await createEstimate(fd).unwrap();
      onCreated(res.check_id);
      setObjectName('');
      setSmetaCode('');
      setRegion('');
      setObjectType('');
      setFile(null);
    } catch (err) {
      setError(
        apiErrorText(
          err,
          'Не удалось запустить расчёт. Проверьте формат файла и попробуйте снова.',
        ),
      );
      // eslint-disable-next-line no-console
      console.error('createTenderEstimate failed', err);
    }
  }

  return (
    <Card>
      <form onSubmit={handleSubmit}>
        <Field>
          <Label>Объект</Label>
          <Input
            placeholder="Например: Котельная, корпус КЖ"
            value={objectName}
            onChange={(e) => setObjectName(e.target.value)}
          />
        </Field>
        <Field>
          <Label>Привязка к преддоговору / договору / объекту ПТО (необязательно)</Label>
          <FormRow>
            <FormSelect
              value={precontractId}
              onChange={(e) => setPrecontractId(e.target.value)}
            >
              <option value="">— преддоговор (тендер) —</option>
              {contextPrecontracts.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.subject || p.counterparty || p.id.slice(0, 8)}
                </option>
              ))}
            </FormSelect>
            <FormSelect
              value={contractId}
              onChange={(e) => setContractId(e.target.value)}
            >
              <option value="">— договор —</option>
              {contextContracts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.contract_number ? `№ ${c.contract_number}` : 'договор б/н'} ·{' '}
                  {(c.subject || c.counterparty || '').slice(0, 40)}
                </option>
              ))}
            </FormSelect>
            <FormSelect
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
            >
              <option value="">— проект ПТО —</option>
              {contextProjects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code ? `${p.code} · ` : ''}
                  {p.name}
                </option>
              ))}
            </FormSelect>
          </FormRow>
        </Field>
        <Field>
          <Label>Шифр сметы (необязательно)</Label>
          <Input
            placeholder="Например: 02-01-01"
            value={smetaCode}
            onChange={(e) => setSmetaCode(e.target.value)}
          />
        </Field>
        <Field>
          <Label>Регион (необязательно)</Label>
          <Input
            placeholder="Субъект РФ, как в ФГИС ЦС — для приведения цен"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
          />
        </Field>
        <Field>
          <Label>Тип объекта (необязательно)</Label>
          <Input
            placeholder="Тип объекта строительства — ключ БИМ-индекса ФЕР"
            value={objectType}
            onChange={(e) => setObjectType(e.target.value)}
          />
        </Field>
        <Field>
          <Label>Формат входа</Label>
          <Select value={inputKind} onChange={(e) => setInputKind(e.target.value)}>
            <option value="grand_smeta_skeleton">Скелет Гранд-Сметы (шифры без цен)</option>
            <option value="vor">Ведомость объёмов работ (ВОР, без шифров)</option>
            <option value="volume_check">Из проверки объёмов (Функция 1 — РД или контракт)</option>
          </Select>
        </Field>
        <Field>
          <Label>Что считаем</Label>
          <Select value={calcMode} onChange={(e) => setCalcMode(e.target.value)}>
            <option value="works_and_materials">Работы и материалы</option>
            <option value="works_only">
              Только работы — без материалов, машины и перевозки считаем
            </option>
          </Select>
        </Field>
        {inputKind === 'volume_check' ? (
          <>
            <Field>
              <Label>Проверка объёмов (Функция 1)</Label>
              <Select value={volumeCheckId} onChange={(e) => setVolumeCheckId(e.target.value)}>
                <option value="">— выберите завершённую проверку объёмов —</option>
                {volumeChecks.map((vc) => (
                  <option key={vc.id} value={vc.id}>
                    {vc.object_name} ({new Date(vc.created_at).toLocaleDateString('ru-RU')})
                  </option>
                ))}
              </Select>
            </Field>
            <Field>
              <Label>Сторона объёмов</Label>
              <Select value={volumeSource} onChange={(e) => setVolumeSource(e.target.value)}>
                <option value="rd">По рабочей документации (РД)</option>
                <option value="contract">По ведомости контракта (ВОР / РДЦ)</option>
              </Select>
            </Field>
          </>
        ) : (
          <Field>
            <Label>Перечень работ тендера</Label>
            <Input
              type="file"
              accept=".xlsx,.xlsm,.pdf"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <FileHint>
              {file
                ? `Выбран файл: ${file.name}`
                : 'Excel-выгрузка Гранд-Сметы или ВОР (Excel/PDF).'}
            </FileHint>
          </Field>
        )}
        {error && <Err>{error}</Err>}
        <SubmitWrapper>
          <Button
            type="submit"
            $variant="primary"
            disabled={creating || !can_write}
            title={!can_write ? 'Только чтение' : undefined}
          >
            {creating ? 'Запуск…' : 'Рассчитать ЛСР'}
          </Button>
        </SubmitWrapper>
      </form>
    </Card>
  );
}
